import type {
  DesignElement,
  ImageElement,
  LineElement,
  ShapeElement,
  TableElement,
  CellTableElement,
  VoucherTableElement,
  VoucherTableColumn,
  VoucherMetaField,
  TextElement,
  PrintFormat,
  PaperSetup,
} from '@/types';
import { uid } from './id';

let zCounter = 1;
export function nextZ(): number {
  return ++zCounter;
}

/**
 * Return a unique layer name like "text-1", "text-2", "shape-3" given the
 * existing elements. The base prefix is taken from the element's kind
 * (rectangle/ellipse get distinct prefixes, textbox is its own bucket).
 */
export function uniqueLayerName(
  existing: { name?: string }[],
  prefix: string,
): string {
  const taken = new Set(
    existing.map((e) => (e.name ?? '').toLowerCase()).filter(Boolean),
  );
  let i = 1;
  // Cap at a sane upper bound; in practice users add far fewer.
  while (i < 10_000) {
    const candidate = `${prefix}-${i}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
    i++;
  }
  return `${prefix}-${Date.now()}`;
}

export function defaultPaper(): PaperSetup {
  return {
    size: 'A4',
    orientation: 'portrait',
    margins: { top: 15, right: 15, bottom: 15, left: 15 },
  };
}

/**
 * The height (mm) an item table should occupy so its body divides into exactly
 * `itemsPerPage` rows of `rowMinHeight` each, plus one extra row of the same
 * height for the header and/or the summary/total row when those are enabled:
 *
 *   height = rowMinHeight × (itemsPerPage + showHeader + showSummary)
 *
 * Returns `null` when no fixed per-page row count is configured (itemsPerPage
 * unset/0) — callers should then leave the table's manual height untouched.
 */
export function autoTableHeightMm(
  el: TableElement,
  itemsPerPage: number | undefined,
): number | null {
  if (!itemsPerPage || itemsPerPage <= 0) return null;
  const rowH = el.rowMinHeight > 0 ? el.rowMinHeight : 7;
  const headerRows = el.showHeader !== false ? 1 : 0;
  const summaryRows = el.showSummary ? 1 : 0;
  return rowH * (itemsPerPage + headerRows + summaryRows);
}

/**
 * Whether an item table should size its box to its rows (shrink-wrap) rather
 * than to a hand-dragged height. True for every normal table; only the legacy
 * headerless "divide evenly" mode (no header AND no per-page row count) keeps a
 * fixed, manually-resized box that its rows stretch to fill.
 */
export function tableAutoHeight(
  el: TableElement,
  itemsPerPage: number | undefined,
): boolean {
  const divideEvenly =
    el.showHeader === false && !(itemsPerPage && itemsPerPage > 0);
  return !divideEvenly;
}

export function makeText(overrides: Partial<TextElement> = {}): TextElement {
  return {
    id: uid('el'),
    type: 'text',
    name: 'Text',
    content: 'Double-click to edit',
    position: { x: 20, y: 20, width: 80, height: 12, rotation: 0, zIndex: nextZ() },
    opacity: 1,
    fontFamily: 'Inter Tight',
    fontSize: 11,
    fontWeight: 400,
    italic: false,
    underline: false,
    color: '#1a1916',
    align: 'left',
    lineHeight: 1.3,
    letterSpacing: 0,
    ...overrides,
  };
}

export function makeShape(overrides: Partial<ShapeElement> = {}): ShapeElement {
  return {
    id: uid('el'),
    type: 'shape',
    name: 'Shape',
    shape: 'rectangle',
    position: { x: 20, y: 20, width: 40, height: 30, rotation: 0, zIndex: nextZ() },
    opacity: 1,
    fill: '#b6442c',
    fillEnabled: true,
    stroke: '#1a1916',
    strokeWidth: 0,
    strokeEnabled: false,
    borderRadius: 0,
    ...overrides,
  };
}

/**
 * Convenience factory for the "Text Box" tool — same shape as a TextElement
 * but seeded with background + border so it looks like a box right away.
 */
export function makeTextBox(overrides: Partial<TextElement> = {}): TextElement {
  return makeText({
    name: 'TextBox',
    content: 'Text box',
    position: { x: 20, y: 20, width: 80, height: 24, rotation: 0, zIndex: nextZ() },
    background: '#ffffff',
    backgroundEnabled: true,
    borderColor: '#1a1916',
    borderWidth: 1,
    borderEnabled: true,
    borderRadius: 4,
    padding: 2,
    align: 'left',
    ...overrides,
  });
}

export function makeImage(overrides: Partial<ImageElement> = {}): ImageElement {
  return {
    id: uid('el'),
    type: 'image',
    name: 'Image',
    src: '',
    position: { x: 20, y: 20, width: 40, height: 40, rotation: 0, zIndex: nextZ() },
    opacity: 1,
    objectFit: 'contain',
    ...overrides,
  };
}

export function makeLine(overrides: Partial<LineElement> = {}): LineElement {
  return {
    id: uid('el'),
    type: 'line',
    name: 'Line',
    position: { x: 20, y: 50, width: 100, height: 0, rotation: 0, zIndex: nextZ() },
    opacity: 1,
    color: '#1a1916',
    thickness: 0.5,
    style: 'solid',
    ...overrides,
  };
}

export function makeTable(overrides: Partial<TableElement> = {}): TableElement {
  return {
    id: uid('el'),
    type: 'table',
    name: 'Item Table',
    position: { x: 15, y: 80, width: 180, height: 60, rotation: 0, zIndex: nextZ() },
    opacity: 1,
    columns: [
      { id: uid('col'), key: 'sn',            header: 'S.N.',              widthPercent: 6,  headerAlign: 'center', bodyAlign: 'center', format: 'text' },
      { id: uid('col'), key: 'itemName',       header: 'Item\nName',        widthPercent: 20, headerAlign: 'left',   bodyAlign: 'left',   format: 'text' },
      { id: uid('col'), key: 'itemCode',       header: 'Code',              widthPercent: 8,  headerAlign: 'center', bodyAlign: 'center', format: 'text' },
      { id: uid('col'), key: 'purity',         header: 'Purity',            widthPercent: 8,  headerAlign: 'center', bodyAlign: 'center', format: 'text' },
      { id: uid('col'), key: 'grossWeight',    header: 'Gross.wt.\n(gm)',   widthPercent: 8,  headerAlign: 'right',  bodyAlign: 'right',  format: 'number' },
      { id: uid('col'), key: 'netWeight',      header: 'Net.wt.\n(gm)',     widthPercent: 8,  headerAlign: 'right',  bodyAlign: 'right',  format: 'number' },
      { id: uid('col'), key: 'wastageWeight',  header: 'Wastage\nWt.(gm)',  widthPercent: 8,  headerAlign: 'right',  bodyAlign: 'right',  format: 'number' },
      { id: uid('col'), key: 'netWithWastage', header: 'Net w/\nWastage',   widthPercent: 8,  headerAlign: 'right',  bodyAlign: 'right',  format: 'number' },
      { id: uid('col'), key: 'makingAmount',   header: 'Making\nAmount',    widthPercent: 10, headerAlign: 'right',  bodyAlign: 'right',  format: 'currency' },
      { id: uid('col'), key: 'rate',           header: 'Rate\nper gm',      widthPercent: 8,  headerAlign: 'right',  bodyAlign: 'right',  format: 'currency' },
      { id: uid('col'), key: 'netAmount',      header: 'Total\nAmount',     widthPercent: 8,  headerAlign: 'right',  bodyAlign: 'right',  format: 'currency' },
    ],
    showHeader: true,
    headerFontFamily: 'Inter Tight',
    headerFontSize: 10,
    headerFontWeight: 600,
    headerColor: '#1a1916',
    headerBackground: '#f0ead8',
    bodyFontFamily: 'Inter Tight',
    bodyFontSize: 9.5,
    bodyColor: '#33312a',
    borderColor: '#9a9582',
    borderWidth: 0.3,
    rowDivider: true,
    columnDivider: false,
    zebra: false,
    zebraColor: '#f9f6ee',
    rowMinHeight: 7,
    showSummary: false,
    ...overrides,
  };
}

export function makeCellTable(
  rows = 3,
  columnCount = 3,
  overrides: Partial<CellTableElement> = {},
): CellTableElement {
  const cells: string[][] = Array.from({ length: rows }, () =>
    Array.from({ length: columnCount }, () => ''),
  );
  const colWidth = 100 / columnCount;
  return {
    id: uid('el'),
    type: 'cellTable',
    name: 'Table',
    position: { x: 20, y: 20, width: 120, height: 40, rotation: 0, zIndex: nextZ() },
    opacity: 1,
    rows,
    columns: Array.from({ length: columnCount }, () => ({ widthPercent: colWidth })),
    cells,
    fontFamily: 'Inter Tight',
    fontSize: 10,
    color: '#33312a',
    textAlign: 'left',
    cellPadding: 4,
    borderColor: '#9a9582',
    borderWidth: 0.3,
    showRowBorders: true,
    showColumnBorders: true,
    zebra: false,
    zebraColor: '#f9f6ee',
    ...overrides,
  };
}

export function makeVoucherTable(
  overrides: Partial<VoucherTableElement> = {},
): VoucherTableElement {
  return {
    id: uid('el'),
    type: 'voucherTable',
    name: 'Voucher Table',
    position: { x: 15, y: 80, width: 180, height: 90, rotation: 0, zIndex: nextZ() },
    opacity: 1,

    showTitle: true,
    title: '',
    showMeta: true,
    metaFields: [
      { id: uid('meta'), label: 'Voucher No.', key: 'voucher_number' },
      { id: uid('meta'), label: 'Date', key: 'voucher_date' },
    ],

    columns: [
      { id: uid('col'), key: 'particular', header: 'Particulars',  widthPercent: 42, align: 'left',  format: 'text' },
      { id: uid('col'), key: 'debit',      header: 'Debit (Rs.)',  widthPercent: 19, align: 'right', format: 'currency' },
      { id: uid('col'), key: 'credit',     header: 'Credit (Rs.)', widthPercent: 19, align: 'right', format: 'currency' },
      { id: uid('col'), key: 'narration',  header: 'Narration',    widthPercent: 20, align: 'left',  format: 'text' },
    ],

    stripLedgerBalance: true,
    drPrefix: 'Dr',
    crPrefix: 'Cr',

    showTotals: true,
    totalsLabel: '',
    showAmountInWords: true,
    showLongNarration: true,
    narrationKey: 'narration',
    showSignatures: true,
    signatures: ['Prepared By', 'Verified By', 'Authorised By'],

    fontFamily: 'Inter Tight',
    fontSize: 10,
    titleFontSize: 13,
    headerFontWeight: 700,
    color: '#1a1916',
    borderColor: '#1a1916',
    borderWidth: 0.5,
    headerBackground: '#ffffff',
    cellPadding: 4,
    ...overrides,
  };
}

export function resolveVoucherColumns(el: VoucherTableElement): VoucherTableColumn[] {
  if (el.columns) return el.columns;
  const cols: VoucherTableColumn[] = [
    { id: 'legacy-particular', key: 'particular', header: el.particularsHeader ?? 'Particulars',  widthPercent: el.particularsWidth ?? 42, align: 'left',  format: 'text' },
    { id: 'legacy-debit',      key: 'debit',      header: el.debitHeader ?? 'Debit (Rs.)',        widthPercent: el.debitWidth ?? 19,       align: 'right', format: 'currency' },
    { id: 'legacy-credit',     key: 'credit',     header: el.creditHeader ?? 'Credit (Rs.)',      widthPercent: el.creditWidth ?? 19,      align: 'right', format: 'currency' },
  ];
  if (el.showNarration ?? true) {
    cols.push({ id: 'legacy-narration', key: 'narration', header: el.narrationHeader ?? 'Narration', widthPercent: el.narrationWidth ?? 20, align: 'left', format: 'text' });
  }
  return cols;
}

export function resolveVoucherMetaFields(el: VoucherTableElement): VoucherMetaField[] {
  if (el.metaFields) return el.metaFields;
  return [
    { id: 'legacy-no',   label: el.voucherNoLabel ?? 'Voucher No.', key: 'voucher_number' },
    { id: 'legacy-date', label: el.dateLabel ?? 'Date',             key: 'voucher_date' },
  ];
}

export function makeFormat(businessId: string, name = 'Untitled Format'): PrintFormat {
  return {
    id: uid('fmt'),
    businessId,
    name,
    paper: defaultPaper(),
    background: '#ffffff',
    elements: [],
    version: 1,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

export function duplicateElement(el: DesignElement): DesignElement {
  return {
    ...el,
    id: uid('el'),
    position: { ...el.position, x: el.position.x + 5, y: el.position.y + 5, zIndex: nextZ() },
  };
}
